import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import Switch from 'react-switch'
import {
  Button,
  ButtonGroup,
  Col,
  FormGroup,
  Input,
  Row,
  Alert,
} from 'reactstrap'
import { useFormUtils } from 'views/shared/useFormUtils'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'

import {
  fruitTypeOptions,
  vegTypeOptions,
  packageTypeOptions,
  productTypeOptions,
} from './constants'
import PropTypes from 'prop-types'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'

const FrozenVegsFruitsPackageReview = ({ item, isDisabled }) => {
  const [packaging, setPackaging] = useState({
    fKCategory: item.fKCategory,
    fkSubCategory: item.fkSubCategory,
    FrozenProductType: item.FrozenProductType,
    Size: item.Size,
    PackageType: item.PackageType,
    ItemType: item.ItemType,
    FrozenPackageCertification: item.FrozenPackageCertification,
    OrganicVerification: item.OrganicVerification,
  })
  const [typeSelectOptions, setTypeSelectOptions] = useState(fruitTypeOptions)

  // Setup the "Product Type" select and "Type" select controls
  // based upon the current type of product

  const methods = useFormContext()
  const {
    setSelectReviewer,
    setSwitchReviewer,
    onChangeReviewer,
    asterisk,
  } = useFormUtils()

  useEffect(() => {
    console.log('item is: ' + JSON.stringify(item))
    if (item !== null) {
      const {
        category,
        subCategory,
        FrozenProductType,
        Size,
        PackageType,
        ItemType,
        FrozenPackageCertification,
        OrganicVerification,
      } = item
      setPackaging({
        category,
        subCategory,
        FrozenProductType,
        Size,
        PackageType,
        ItemType,
        FrozenPackageCertification,
        OrganicVerification,
      })
      setTypeSelectOptions(
        item.FrozenProductType === 'Fruit' ? fruitTypeOptions : vegTypeOptions
      )
    } // eslint-disable-next-line
    }, [item])

  // When "Product Type" selection changed
  // also change the "Type" selection options

  const tempType = {
    value: item.ItemType,
    label: item.ItemType,
  }
  const tempPackageType = {
    value: item.PackageType,
    label: item.PackageType,
  }

  let containsType
  const containsPackageType = packageTypeOptions.some(e => {
    return JSON.stringify(tempPackageType) === JSON.stringify(e)
  })

  if (item.FrozenProductType === 'Fruit') {
    containsType = fruitTypeOptions.some(e => {
      return JSON.stringify(tempType) === JSON.stringify(e)
    })
  } else {
    containsType = vegTypeOptions.some(e => {
      return JSON.stringify(tempType) === JSON.stringify(e)
    })
  }

  if (containsType) {
    packaging.ItemType = item.ItemType
  } else {
    packaging.ItemType = 'Other'
    packaging.otherType = item.ItemType
    item.otherType = item.ItemType
    item.ItemType = 'Other'
  }
  if (containsPackageType) {
    packaging.PackageType = item.PackageType
  } else {
    packaging.PackageType = 'Other'
    packaging.otherPackageType = item.PackageType
    item.otherPackageType = item.PackageType
    item.PackageType = 'Other'
  }

  return (
    <Row>
      <Col xs={12} md={6} lg={6}>
        <p>
          <strong>Frozen Product Type {asterisk}</strong>
        </p>
        <ButtonGroup>
          <Button
            outline
            color="success"
            className="btn-round"
            active={item.FrozenProductType === productTypeOptions[0]}
            disabled={item.FrozenProductType !== productTypeOptions[0]}
            size="sm"
            onChange={() => {
              setPackaging({
                ...packaging,
                fkSubCategory: '001FF',
              })
            }}
          >
            {productTypeOptions[0]}
          </Button>
        </ButtonGroup>
        <ButtonGroup>
          <Button
            outline
            color="success"
            className="btn-round"
            active={item.FrozenProductType === productTypeOptions[1]}
            disabled={item.FrozenProductType !== productTypeOptions[1]}
            size="sm"
            onChange={() => {
              setPackaging({
                ...packaging,
                fkSubCategory: '001FV',
              })
            }}
          >
            {productTypeOptions[1]}
          </Button>
        </ButtonGroup>
        <br />
        <p>
          <strong>Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={typeSelectOptions}
          defaultValue={{ label: item.ItemType, value: item.ItemType }}
          name="ItemType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Type'}
          onChange={([selected, value]) => {
            setSelectReviewer(item, selected, value)
            setPackaging({
              ...packaging,
              ItemType: value,
            })
            return selected
          }}
          isDisabled={isDisabled}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="ItemType"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        {item?.ItemType === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="otherType"
              placeholder={'Other Type'}
              defaultValue={item.otherType}
              control={methods.control}
              rules={{ required: true }}
              onKeyDown={e => {
                if (isNotAlpha(e.key)) {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="otherType"
                message={'Other type is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <FormGroup>
          <p>
            <strong>Size {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            name="Size"
            placeholder={'Size'}
            defaultValue={item.Size}
            control={methods.control}
            rules={{ required: true, maxLength: 5 }}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            onChange={([event]) => {
              onChangeReviewer(item, event)
              return event
            }}
            disabled={isDisabled}
          />
          <p className={'text-danger'}>
            {methods.errors.Size && methods.errors.Size.type === 'required' && (
              <Alert color="danger" className={'mt-3'}>
                ⚠ Size is required.
              </Alert>
            )}
            {methods.errors.Size && methods.errors.Size.type === 'maxLength' && (
              <Alert color="danger" className={'mt-3'}>
                ⚠ Size exceeds 5 characters.
              </Alert>
            )}
          </p>
        </FormGroup>
      </Col>
      <Col xs={12} md={6} lg={6}>
        <p>
          <strong>Package Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={packageTypeOptions}
          defaultValue={{ label: item.PackageType, value: item.PackageType }}
          name="PackageType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Package Type'}
          onChange={([selected, value]) => {
            setSelectReviewer(item, selected, value)
            setPackaging({
              ...packaging,
              PackageType: value,
            })
            return selected
          }}
          isDisabled={isDisabled}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="PackageType"
            message={'⚠ Package type is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        {item?.PackageType === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Package Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="otherPackageType"
              placeholder={'Other Package Type'}
              defaultValue={item.otherPackageType}
              control={methods.control}
              rules={{ required: true }}
              onKeyDown={e => {
                if (isNotAlpha(e.key)) {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="otherPackageType"
                message={'Other package type is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <hr />
        <p>
          <strong>Organic</strong>
        </p>
        <Controller
          as={Switch}
          name="OrganicVerification"
          id="OrganicVerification"
          onChange={([checked, event, id]) => {
            setSwitchReviewer(item, checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={item.OrganicVerification}
          disabled={isDisabled}
        />
        <hr />
        <p>
          <strong>
            I certify that this product has NO added sugars, added ingredients,
            or artificial sweeteners, such as Splenda or Nutrasweet. {asterisk}
          </strong>
        </p>
        <Controller
          as={Switch}
          name="FrozenPackageCertification"
          id="FrozenPackageCertification"
          onChange={([checked, event, id]) => {
            setSwitchReviewer(item, checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={item.FrozenPackageCertification}
          rules={{
            validate: FrozenPackageCertification =>
              FrozenPackageCertification === true,
          }}
          disabled={isDisabled}
        />
        <br />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="FrozenPackageCertification"
            message={
              '⚠ Product must have NO added sugars, ingredients, or artificial sweeteners.'
            }
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
      </Col>
    </Row>
  )
}

FrozenVegsFruitsPackageReview.propTypes = {
  item: PropTypes.object,
  isDisabled: PropTypes.bool,
}

export default FrozenVegsFruitsPackageReview
